import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { referencia } from '../risk-applications/textosDoDestino';
import type { Efetivos, IdDoBanco } from '../../services/efetivosService';
import type { NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { AcaoEmLink } from './AcaoEmLink';
import { contar, flexionar, mesmoId } from './textosDoNivel';
import type { ConfigDeAplicacao, DestinoRef } from './tipos';

/** Quantos itens aparecem antes do "Ver mais": o catálogo pode ter centenas. */
export const VISIVEIS_NO_PAINEL = 30;

const TODOS = '';
const OUTROS = '__outros__';

const PELO_NIVEL: Record<NivelDeAplicacao, string> = {
  company: 'pela empresa',
  unit: 'pela unidade',
  department: 'pelo setor',
  occupation: 'pelo cargo',
  employee: 'pelo funcionário',
};

export interface PainelDeIncluirProps<Item, A, V> {
  config: ConfigDeAplicacao<Item, A, V>;
  aberto: boolean;
  catalogo: Item[] | null;
  destino: DestinoRef;
  /** O que vale para o destino; null enquanto não chegou. */
  efetivos: Efetivos<A> | null;
  empresaId: IdDoBanco | null;
  /** A inclusão está a caminho: o botão espera. */
  incluindo: boolean;
  aoFechar: () => void;
  aoIncluir: (itens: Item[]) => void;
}

/**
 * O catálogo, num painel largo à direita (#606, opção D): a tabela do destino continua à vista.
 * A pessoa marca vários itens e inclui de uma vez; o que o destino já tem aparece marcado e
 * travado, dizendo se é dele ou se já vale por vir de cima. Documentos não se perguntam aqui:
 * o item entra saindo em todos.
 */
export function PainelDeIncluir<Item, A, V>({
  config,
  aberto,
  catalogo,
  destino,
  efetivos,
  empresaId,
  incluindo,
  aoFechar,
  aoIncluir,
}: PainelDeIncluirProps<Item, A, V>) {
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState(TODOS);
  const [todosVisiveis, setTodosVisiveis] = useState(false);
  const [marcados, setMarcados] = useState<string[]>([]);
  const { plural, singular, feminino } = config.rotulos;

  const proprioDe = (item: Item) =>
    (efetivos?.proprias ?? []).some((a) => mesmoId(config.aplicacao.itemId(a), config.item.id(item)));
  const herdadaDe = (item: Item) =>
    (efetivos?.herdadas ?? []).find(
      (herdada) => !herdada.substituidaNoDestino && mesmoId(config.aplicacao.itemId(herdada), config.item.id(item))
    );

  const termo = busca.trim().toLocaleLowerCase('pt-BR');
  const lista = catalogo ?? [];
  const filtrados = lista.filter((item) => {
    const doItem = config.item.tipo?.(item) ?? null;
    if (tipo === OUTROS && config.tipos.some((t) => t.valor === doItem)) return false;
    if (tipo !== TODOS && tipo !== OUTROS && doItem !== tipo) return false;
    if (!termo) return true;
    return [config.item.nome(item), config.item.perigo?.(item), config.item.descricao(item)].some((texto) =>
      (texto ?? '').toLocaleLowerCase('pt-BR').includes(termo)
    );
  });

  const tiposPresentes = config.tipos.filter((t) => lista.some((item) => config.item.tipo?.(item) === t.valor));
  const grupos =
    config.tipos.length === 0
      ? [{ id: TODOS, titulo: null as string | null, itens: filtrados }]
      : [
          ...tiposPresentes.map((t) => ({
            id: t.valor,
            titulo: t.plural ?? t.rotulo,
            itens: filtrados.filter((item) => config.item.tipo?.(item) === t.valor),
          })),
          {
            id: OUTROS,
            titulo: 'Outros',
            itens: filtrados.filter((item) => !config.tipos.some((t) => t.valor === config.item.tipo?.(item))),
          },
        ].filter((grupo) => grupo.itens.length > 0);

  let restantes = todosVisiveis ? Number.POSITIVE_INFINITY : VISIVEIS_NO_PAINEL;
  const gruposVisiveis = grupos
    .map((grupo) => {
      const itens = grupo.itens.slice(0, Math.max(restantes, 0));
      restantes -= itens.length;
      return { ...grupo, itens };
    })
    .filter((grupo) => grupo.itens.length > 0);
  const escondidos = filtrados.length - gruposVisiveis.reduce((soma, grupo) => soma + grupo.itens.length, 0);

  const alternar = (id: string) =>
    setMarcados((atual) => (atual.includes(id) ? atual.filter((m) => m !== id) : [...atual, id]));
  const escolhidos = lista.filter((item) => marcados.includes(String(config.item.id(item))));
  const fechar = () => {
    setMarcados([]);
    setBusca('');
    aoFechar();
  };
  const incluir = () => {
    aoIncluir(escolhidos);
    setMarcados([]);
  };

  const titulo = `Incluir ${plural} ${referencia('em', destino.nivel, destino.nome)}`;
  const { AvisoDosItens } = config;

  return (
    <Drawer anchor="right" open={aberto} onClose={fechar} PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}>
      <Box component="section" aria-label={titulo} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {titulo}
          </Typography>
          {AvisoDosItens && <AvisoDosItens destino={destino} empresaId={empresaId} />}
          <TextField
            fullWidth
            autoFocus
            label={`Buscar ${plural} pelo nome, perigo ou descrição`}
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setTodosVisiveis(false);
            }}
            sx={{ mb: 1.5 }}
          />
          {tiposPresentes.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {[{ valor: TODOS, rotulo: 'Todos', plural: 'Todos' }, ...tiposPresentes].map((opcao) => (
                <Chip
                  key={opcao.valor || 'todos'}
                  label={opcao.plural ?? opcao.rotulo}
                  color={tipo === opcao.valor ? 'primary' : 'default'}
                  variant={tipo === opcao.valor ? 'filled' : 'outlined'}
                  aria-pressed={tipo === opcao.valor}
                  onClick={() => {
                    setTipo(opcao.valor);
                    setTodosVisiveis(false);
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {catalogo === null && (
            <Typography variant="body2" color="text.secondary">
              Carregando o catálogo de {plural}…
            </Typography>
          )}
          {catalogo !== null && filtrados.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {termo || tipo !== TODOS
                ? `${flexionar(feminino, 'Nenhum')} ${singular} do catálogo combina com a busca. Tente outro nome ou outro filtro.`
                : `O catálogo de ${plural} ainda está vazio para o seu acesso.`}
            </Typography>
          )}
          <Stack spacing={2}>
            {gruposVisiveis.map((grupo) => (
              <Box key={grupo.id || 'lista'}>
                {grupo.titulo && (
                  <Typography variant="overline" color="text.secondary" component="h3">
                    {grupo.titulo}
                  </Typography>
                )}
                <Stack spacing={0.5}>
                  {grupo.itens.map((item) => {
                    const id = String(config.item.id(item));
                    const nome = config.item.nome(item);
                    const descricao = config.item.descricao(item);
                    const exemplo = config.item.perigo?.(item);
                    const proprio = proprioDe(item);
                    const herdada = proprio ? undefined : herdadaDe(item);
                    const travado = proprio || herdada !== undefined;
                    let sinal: string | null = null;
                    if (proprio) sinal = `já ${flexionar(feminino, 'aplicado')}`;
                    else if (herdada) sinal = `já vale ${PELO_NIVEL[herdada.origem.nivel]}`;
                    return (
                      <FormControlLabel
                        key={id}
                        sx={{ alignItems: 'flex-start', mx: 0, py: 0.5 }}
                        control={
                          <Checkbox
                            checked={travado || marcados.includes(id)}
                            disabled={travado}
                            onChange={() => alternar(id)}
                            inputProps={{ 'aria-label': nome }}
                            sx={{ pt: 0.25 }}
                          />
                        }
                        label={
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" fontWeight={600}>
                                {nome}
                              </Typography>
                              {sinal && (
                                <Typography variant="body2" color="text.secondary">
                                  {sinal}
                                </Typography>
                              )}
                            </Stack>
                            {descricao && (
                              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 560 }}>
                                {descricao}
                              </Typography>
                            )}
                            {exemplo && (
                              <Typography variant="body2" color="text.secondary">
                                {`Ex.: ${exemplo}`}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Stack>
          {escondidos > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <AcaoEmLink onClick={() => setTodosVisiveis(true)}>Ver mais {escondidos}</AcaoEmLink>
              <Typography variant="body2" color="text.secondary">
                ou escreva na busca para achar direto
              </Typography>
            </Stack>
          )}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            {contar(escolhidos.length, flexionar(feminino, 'marcado'), flexionar(feminino, 'marcados'))}
          </Typography>
          <Button onClick={fechar} disabled={incluindo}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={incluir} disabled={incluindo || escolhidos.length === 0}>
            {incluindo ? 'Incluindo…' : `Incluir ${contar(escolhidos.length, singular, plural)}`}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
